import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button, FormControl, FormGroup, ControlLabel, Tabs, Tab, Modal, Label } from 'react-bootstrap';
import Utils from '../util/Utils.jsx';
import { useStateValue } from '../context/state.jsx';
import { openAlert } from '../context/alert.js';
import { setIsSuperAdmin } from '../context/isSuperAdmin.js';
import ProjectDestroyModal from '../components/superadmin/ProjectDestroyModal.jsx';
import AiInsightsTab from '../components/superadmin/AiInsightsTab.jsx';
import NarrativeQueriesTab from '../components/superadmin/NarrativeQueriesTab.jsx';

/**
 * Super Admin dashboard — bulk user ops, multi-select project destruction, maintenance.
 * Server enforces access; client visibility is not a security boundary.
 */
const SuperAdminPage = () => {
	const [{ tokenExpired }, dispatch] = useStateValue();
	const [authorized, setAuthorized] = useState(null);
	const [adminTab, setAdminTab] = useState(1);
	const [users, setUsers] = useState([]);
	const [projects, setProjects] = useState([]);
	const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
	const [selectedProjectIds, setSelectedProjectIds] = useState(() => new Set());
	const [projectFilter, setProjectFilter] = useState('all');
	const [importText, setImportText] = useState('email,firstName,lastName\n');
	const [importMode, setImportMode] = useState('csv');
	const [destroyTarget, setDestroyTarget] = useState(null);
	const [bulkDestroyOpen, setBulkDestroyOpen] = useState(false);
	const [bulkConfirm, setBulkConfirm] = useState('');
	const [bulkUserActionOpen, setBulkUserActionOpen] = useState(false);
	const [bulkProjectId, setBulkProjectId] = useState('');
	const [bulkAddToProject, setBulkAddToProject] = useState(true);
	const [bulkResetPasswords, setBulkResetPasswords] = useState(true);
	const [busy, setBusy] = useState(false);

	// Maintenance state
	const [idleDays, setIdleDays] = useState(365);
	const [inactivePreview, setInactivePreview] = useState(null);
	const [orphanPreview, setOrphanPreview] = useState(null);
	const [maintStep, setMaintStep] = useState('preview');
	const [purgeUsersConfirm, setPurgeUsersConfirm] = useState('');
	const [purgeOrphansConfirm, setPurgeOrphansConfirm] = useState('');

	const loadUsers = useCallback(async () => {
		const result = await Utils.ajaxAsync('adminListUsers', {}, dispatch, { suppressErrorAlert: true });
		if (!result.success) {
			setAuthorized(false);
			dispatch(setIsSuperAdmin(false));
			return;
		}
		setAuthorized(true);
		dispatch(setIsSuperAdmin(true));
		setUsers(result.users || []);
		setSelectedUserIds(new Set());
	}, [dispatch]);

	const loadProjects = useCallback(async () => {
		const result = await Utils.ajaxAsync('adminListProjects', {}, dispatch, { suppressErrorAlert: true });
		if (result.success) {
			setProjects(result.projects || []);
			setSelectedProjectIds(new Set());
		}
	}, [dispatch]);

	useEffect(() => {
		if (!localStorage.getItem('token')) {
			setAuthorized(false);
			return;
		}
		(async () => {
			try {
				await loadUsers();
				await loadProjects();
			} catch (e) {
				setAuthorized(false);
			}
		})();
	}, [loadUsers, loadProjects]);

	const toggleInSet = (setter, id) => {
		setter(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const formatDate = (ts) => {
		if (!ts) return '—';
		try {
			return new Date(ts * 1000).toLocaleString();
		} catch (e) {
			return '—';
		}
	};

	const filteredProjects = useMemo(() => {
		let list = [...projects];
		if (projectFilter === 'orphans') list = list.filter(p => p.isOrphan);
		else if (projectFilter === 'shared') list = list.filter(p => p.isShared);
		else if (projectFilter === 'unshared') list = list.filter(p => !p.isShared && !p.isOrphan);
		return list.sort((a, b) => (b.created || 0) - (a.created || 0));
	}, [projects, projectFilter]);

	const selectableFilteredIds = useMemo(
		() => filteredProjects.filter(p => p.id !== 1).map(p => p.id),
		[filteredProjects]
	);

	const allFilteredSelected = selectableFilteredIds.length > 0 &&
		selectableFilteredIds.every(id => selectedProjectIds.has(id));

	const toggleSelectAllProjects = () => {
		setSelectedProjectIds(prev => {
			const next = new Set(prev);
			if (allFilteredSelected) {
				selectableFilteredIds.forEach(id => next.delete(id));
			} else {
				selectableFilteredIds.forEach(id => next.add(id));
			}
			return next;
		});
	};

	const selectOrphans = () => {
		setSelectedProjectIds(new Set(projects.filter(p => p.isOrphan).map(p => p.id)));
		setProjectFilter('orphans');
	};

	const selectedProjectCount = selectedProjectIds.size;
	const selectedProjects = useMemo(
		() => projects.filter(p => selectedProjectIds.has(p.id)),
		[projects, selectedProjectIds]
	);
	const selectedSharedCount = selectedProjects.filter(p => p.isShared).length;

	const handleBulkDeleteUsers = async () => {
		if (!selectedUserIds.size) return;
		const confirmed = window.confirm(`Delete ${selectedUserIds.size} user(s)? This cannot be undone.`);
		if (!confirmed) return;
		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminBulkDeleteUsers', {
				userIdsJson: JSON.stringify([...selectedUserIds])
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				dispatch(openAlert(
					'Bulk delete complete',
					`Deleted ${result.deletedCount}. Errors: ${(result.errors || []).length}.`
				));
				await loadUsers();
			} else {
				dispatch(openAlert('Error', result.error || 'Bulk delete failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Bulk delete failed.'));
		} finally {
			setBusy(false);
		}
	};

	const handleBulkImport = async () => {
		setBusy(true);
		try {
			const payload = importMode === 'json'
				? { usersJson: importText }
				: { usersCsv: importText };
			const result = await Utils.ajaxAsync('adminBulkImportUsers', payload, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				dispatch(openAlert(
					'Bulk import complete',
					`Imported ${result.importedCount}. Skipped ${(result.skipped || []).length}. Errors: ${(result.errors || []).length}.`
				));
				await loadUsers();
			} else {
				dispatch(openAlert('Error', result.error || 'Import failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Import failed.'));
		} finally {
			setBusy(false);
		}
	};

	const openBulkUserActions = () => {
		if (!selectedUserIds.size) return;
		setBulkAddToProject(true);
		setBulkResetPasswords(true);
		setBulkProjectId(projects[0] ? String(projects[0].id) : '');
		setBulkUserActionOpen(true);
	};

	const handleBulkUserActions = async () => {
		if (!selectedUserIds.size) return;
		if (!bulkAddToProject && !bulkResetPasswords) {
			dispatch(openAlert('Nothing selected', 'Choose add to project and/or password reset.'));
			return;
		}
		if (bulkAddToProject && !bulkProjectId) {
			dispatch(openAlert('Project required', 'Select a project to add users to.'));
			return;
		}
		const projectLabel = projects.find(p => String(p.id) === String(bulkProjectId))?.name || `#${bulkProjectId}`;
		const parts = [];
		if (bulkAddToProject) parts.push(`add ${selectedUserIds.size} user(s) to "${projectLabel}"`);
		if (bulkResetPasswords) parts.push(`email password-reset links to ${selectedUserIds.size} user(s)`);
		if (!window.confirm(`Confirm: ${parts.join(' and ')}?`)) return;

		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminBulkProjectUsers', {
				userIdsJson: JSON.stringify([...selectedUserIds]),
				projectId: bulkProjectId,
				addToProject: bulkAddToProject ? '1' : '0',
				resetPasswords: bulkResetPasswords ? '1' : '0'
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				const lines = [];
				if (bulkAddToProject) {
					lines.push(`Added: ${result.addedCount}. Already members: ${result.alreadyMemberCount}.`);
				}
				if (bulkResetPasswords) {
					lines.push(`Password reset emails sent: ${result.resetCount}.`);
				}
				if ((result.errors || []).length) {
					lines.push(`Errors: ${result.errors.length}.`);
				}
				dispatch(openAlert('Bulk user actions complete', lines.join(' ')));
				setBulkUserActionOpen(false);
				await loadUsers();
			} else {
				dispatch(openAlert('Error', result.error || 'Bulk user actions failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Bulk user actions failed.'));
		} finally {
			setBusy(false);
		}
	};

	const handleBulkDestroyProjects = async () => {
		if (bulkConfirm !== 'DESTROY') {
			dispatch(openAlert('Confirmation required', 'Type DESTROY to confirm.'));
			return;
		}
		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminProjectDestroy', {
				action: 'bulkForceDelete',
				projectIdsJson: JSON.stringify([...selectedProjectIds]),
				confirmPhrase: 'DESTROY'
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				dispatch(openAlert(
					'Bulk destroy complete',
					`Deleted ${result.deletedCount}. Skipped ${(result.skipped || []).length}. Errors: ${(result.errors || []).length}.`
				));
				setBulkDestroyOpen(false);
				setBulkConfirm('');
				await loadProjects();
			} else {
				dispatch(openAlert('Error', result.error || 'Bulk destroy failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Bulk destroy failed.'));
		} finally {
			setBusy(false);
		}
	};

	const previewInactive = async () => {
		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminMaintenance', {
				action: 'previewInactiveUsers',
				days: idleDays
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) setInactivePreview(result);
			else dispatch(openAlert('Error', result.error || 'Preview failed.'));
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Preview failed.'));
		} finally {
			setBusy(false);
		}
	};

	const purgeInactive = async () => {
		if (purgeUsersConfirm !== 'PURGE USERS') {
			dispatch(openAlert('Confirmation required', 'Type PURGE USERS to confirm.'));
			return;
		}
		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminMaintenance', {
				action: 'purgeInactiveUsers',
				days: idleDays,
				confirmPhrase: 'PURGE USERS'
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				dispatch(openAlert(
					'Inactive users purged',
					`Users deleted: ${result.deletedUserCount}. Owned projects deleted: ${result.deletedProjectCount}.`
				));
				setInactivePreview(null);
				setPurgeUsersConfirm('');
				setMaintStep('preview');
				await loadUsers();
				await loadProjects();
			} else {
				dispatch(openAlert('Error', result.error || 'Purge failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Purge failed.'));
		} finally {
			setBusy(false);
		}
	};

	const previewOrphans = async () => {
		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminMaintenance', {
				action: 'previewOrphanProjects'
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) setOrphanPreview(result);
			else dispatch(openAlert('Error', result.error || 'Preview failed.'));
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Preview failed.'));
		} finally {
			setBusy(false);
		}
	};

	const purgeOrphans = async () => {
		if (purgeOrphansConfirm !== 'PURGE ORPHANS') {
			dispatch(openAlert('Confirmation required', 'Type PURGE ORPHANS to confirm.'));
			return;
		}
		setBusy(true);
		try {
			const result = await Utils.ajaxAsync('adminMaintenance', {
				action: 'purgeOrphanProjects',
				confirmPhrase: 'PURGE ORPHANS'
			}, dispatch, { suppressErrorAlert: true });
			if (result.success) {
				dispatch(openAlert('Orphans purged', `Deleted ${result.deletedCount} project(s).`));
				setOrphanPreview(null);
				setPurgeOrphansConfirm('');
				setMaintStep('preview');
				await loadProjects();
			} else {
				dispatch(openAlert('Error', result.error || 'Purge failed.'));
			}
		} catch (e) {
			dispatch(openAlert('Error', e.message || 'Purge failed.'));
		} finally {
			setBusy(false);
		}
	};

	const selectInactiveUsersInTable = () => {
		if (!inactivePreview) return;
		const ids = inactivePreview.users.filter(u => !u.protected).map(u => u.id);
		setSelectedUserIds(new Set(ids));
	};

	if (tokenExpired || !localStorage.getItem('token')) {
		return <Navigate to="/signin/" replace />;
	}

	if (authorized === null) {
		return (
			<div className="superAdminPage">
				<div className="pageContent">
					<p>Verifying Super Admin access…</p>
				</div>
			</div>
		);
	}

	if (authorized === false) {
		return (
			<div className="superAdminPage">
				<div className="pageContent">
					<h1>Super Admin</h1>
					<p>You do not have Super Admin access.</p>
					<Link to="/app/">Back to app</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="superAdminPage">
			<div className="pageContent">
				<header className="superAdminHeader">
					<div>
						<h1>Super Admin</h1>
						<p className="superAdminSub">Platform user &amp; project lifecycle controls</p>
					</div>
					<Link className="btn btn-default" to="/app/">Back to app</Link>
				</header>

				<Tabs
					activeKey={adminTab}
					onSelect={key => setAdminTab(Number(key) || 1)}
					id="super-admin-tabs">
					<Tab eventKey={1} title="Users">
						<section className="superAdminSection">
							<h2>Bulk import</h2>
							<p>
								Paste CSV (<code>email,firstName,lastName[,password]</code>) or a JSON array of
								objects with <code>email</code> / <code>emailAddress</code>.
							</p>
							<FormGroup>
								<ControlLabel>Format</ControlLabel>
								<FormControl
									componentClass="select"
									value={importMode}
									onChange={e => setImportMode(e.target.value)}>
									<option value="csv">CSV</option>
									<option value="json">JSON</option>
								</FormControl>
							</FormGroup>
							<FormControl
								componentClass="textarea"
								rows={8}
								value={importText}
								onChange={e => setImportText(e.target.value)}
							/>
							<Button
								bsStyle="primary"
								className="superAdminActionBtn"
								disabled={busy}
								onClick={handleBulkImport}>
								Import users
							</Button>
						</section>

						<section className="superAdminSection">
							<div className="superAdminSectionHead">
								<h2>All users ({users.length})</h2>
								<div className="superAdminToolbar">
									<Button
										bsStyle="primary"
										disabled={busy || !selectedUserIds.size}
										onClick={openBulkUserActions}>
										Add to project / reset passwords ({selectedUserIds.size})
									</Button>
									<Button
										bsStyle="danger"
										disabled={busy || !selectedUserIds.size}
										onClick={handleBulkDeleteUsers}>
										Delete selected ({selectedUserIds.size})
									</Button>
								</div>
							</div>
							<p>
								Select existing users, then add them to a project and/or email password-reset
								links in one step.
							</p>
							<div className="superAdminTableWrap">
								<table className="table table-striped table-condensed superAdminTable">
									<thead>
										<tr>
											<th></th>
											<th>Email</th>
											<th>Name</th>
											<th>Verified</th>
											<th>Admin</th>
											<th>Created</th>
											<th>Last login</th>
										</tr>
									</thead>
									<tbody>
										{users.map(u => (
											<tr key={u.id}>
												<td>
													<input
														type="checkbox"
														checked={selectedUserIds.has(u.id)}
														onChange={() => toggleInSet(setSelectedUserIds, u.id)}
														aria-label={`Select ${u.emailAddress}`}
													/>
												</td>
												<td>{u.emailAddress}</td>
												<td>{[u.firstName, u.lastName].filter(Boolean).join(' ')}</td>
												<td>{u.isVerified ? 'Yes' : 'No'}</td>
												<td>{u.isGroupAdmin ? 'Yes' : '—'}</td>
												<td>{formatDate(u.createDate)}</td>
												<td>{formatDate(u.lastLogin)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					</Tab>

					<Tab eventKey={2} title="Project destruction">
						<section className="superAdminSection">
							<div className="superAdminSectionHead">
								<h2>Projects ({filteredProjects.length}{projectFilter !== 'all' ? ` / ${projects.length}` : ''})</h2>
								<div className="superAdminToolbar">
									<FormControl
										componentClass="select"
										className="superAdminFilterSelect"
										value={projectFilter}
										onChange={e => setProjectFilter(e.target.value)}>
										<option value="all">All projects</option>
										<option value="orphans">Orphans only</option>
										<option value="shared">Shared only</option>
										<option value="unshared">Unshared only</option>
									</FormControl>
									<Button bsSize="small" disabled={busy} onClick={selectOrphans}>
										Select orphans
									</Button>
									<Button
										bsSize="small"
										disabled={busy || !selectedProjectCount}
										onClick={() => setSelectedProjectIds(new Set())}>
										Clear selection
									</Button>
									<Button
										bsStyle="danger"
										bsSize="small"
										disabled={busy || !selectedProjectCount}
										onClick={() => { setBulkConfirm(''); setBulkDestroyOpen(true); }}>
										Destroy selected ({selectedProjectCount})
									</Button>
								</div>
							</div>
							<p>
								Multi-select for quick purge. Single-project <strong>Destroy…</strong> still uses
								the re-assign / force-delete safety modal for shared projects.
							</p>
							<div className="superAdminTableWrap">
								<table className="table table-striped table-condensed superAdminTable">
									<thead>
										<tr>
											<th>
												<input
													type="checkbox"
													checked={allFilteredSelected}
													onChange={toggleSelectAllProjects}
													aria-label="Select all filtered projects"
												/>
											</th>
											<th>ID</th>
											<th>Name</th>
											<th>Owner</th>
											<th>Members</th>
											<th>Flags</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{filteredProjects.map(p => (
											<tr key={p.id} className={p.isOrphan ? 'superAdminOrphanRow' : ''}>
												<td>
													<input
														type="checkbox"
														disabled={p.id === 1}
														checked={selectedProjectIds.has(p.id)}
														onChange={() => toggleInSet(setSelectedProjectIds, p.id)}
														aria-label={`Select project ${p.name}`}
													/>
												</td>
												<td>{p.id}</td>
												<td>{p.name}</td>
												<td>{p.ownerEmail || <em>missing</em>}</td>
												<td>{p.memberCount}</td>
												<td>
													{p.isOrphan && <Label bsStyle="warning">orphan</Label>}{' '}
													{p.isShared && <Label bsStyle="info">shared</Label>}
													{p.id === 1 && <Label>protected</Label>}
												</td>
												<td>
													<Button
														bsStyle="danger"
														bsSize="xsmall"
														disabled={p.id === 1 || busy}
														onClick={() => setDestroyTarget(p)}>
														Destroy…
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					</Tab>

					<Tab eventKey={3} title="AI Insights">
						<AiInsightsTab projects={projects} dispatch={dispatch} />
					</Tab>

					<Tab eventKey={5} title="Narrative queries">
						<NarrativeQueriesTab dispatch={dispatch} />
					</Tab>

					<Tab eventKey={4} title="Maintenance">
						<div className="superAdminDangerZone">
							<div className="superAdminDangerZoneHeader">
								<h2>
									<span className="glyphicon glyphicon-lock leftGlyph" aria-hidden="true"></span>
									Maintenance · Danger Zone
								</h2>
								<p>
									Preview and irreversible Purge are different steps. Project destruction is never
									shown on the preview screen.
								</p>
							</div>
							<ol className="superAdminMaintSteps" aria-label="Maintenance steps">
								<li className={maintStep === 'preview' ? 'isCurrent' : ''}>1 · Preview</li>
								<li className={maintStep === 'purge' ? 'isCurrent isDanger' : ''}>2 · Purge (irreversible)</li>
								<li>3 · Project destruction (separate tab)</li>
							</ol>

							{maintStep === 'preview'
								? (
									<>
										<section className="superAdminSection">
											<h3>Inactive users (preview only)</h3>
											<p>
												Find users whose last activity (last login, or create date if never logged in)
												is older than the threshold. Primary Super Admin and your own account are protected.
												This step does not delete anything.
											</p>
											<div className="superAdminToolbar">
												<FormGroup className="superAdminInlineField">
													<ControlLabel htmlFor="maint-idle-days">Idle days</ControlLabel>
													<FormControl
														id="maint-idle-days"
														type="number"
														min={1}
														value={idleDays}
														onChange={e => setIdleDays(Number(e.target.value) || 365)}
													/>
												</FormGroup>
												<Button bsStyle="primary" disabled={busy} onClick={previewInactive}>
													Preview inactive users
												</Button>
												{inactivePreview && (
													<Button disabled={busy} onClick={() => {
														selectInactiveUsersInTable();
														setAdminTab(1);
													}}>
														Select purgeable in Users tab ({inactivePreview.purgeableCount})
													</Button>
												)}
											</div>
											{inactivePreview && (
												<div>
													<p>
														Found <strong>{inactivePreview.users.length}</strong> inactive
														(&gt;{inactivePreview.days} days);
														<strong> {inactivePreview.purgeableCount}</strong> purgeable.
													</p>
													<div className="superAdminTableWrap">
														<table className="table table-condensed superAdminTable">
															<thead>
																<tr>
																	<th>Email</th>
																	<th>Idle days</th>
																	<th>Last login</th>
																	<th>Protected</th>
																</tr>
															</thead>
															<tbody>
																{inactivePreview.users.map(u => (
																	<tr key={u.id}>
																		<td>{u.emailAddress}</td>
																		<td>{u.idleDays}</td>
																		<td>{formatDate(u.lastLogin)}</td>
																		<td>{u.protected ? 'Yes' : '—'}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</div>
											)}
										</section>

										<section className="superAdminSection">
											<h3>Orphaned projects (preview only)</h3>
											<p>
												Projects with a missing owner user and/or zero members with Access permission
												(excluding the protected sample project id=1). This step does not delete anything.
											</p>
											<Button bsStyle="primary" disabled={busy} onClick={previewOrphans}>
												Preview orphaned projects
											</Button>
											{orphanPreview && (
												<div className="superAdminOrphanBlock">
													<p>Found <strong>{orphanPreview.count}</strong> orphaned project(s).</p>
													<div className="superAdminTableWrap">
														<table className="table table-condensed superAdminTable">
															<thead>
																<tr>
																	<th>ID</th>
																	<th>Name</th>
																	<th>Owner</th>
																	<th>Members</th>
																	<th>Reasons</th>
																</tr>
															</thead>
															<tbody>
																{(orphanPreview.projects || []).map(p => (
																	<tr key={p.id}>
																		<td>{p.id}</td>
																		<td>{p.name}</td>
																		<td>{p.ownerEmail || '—'}</td>
																		<td>{p.memberCount}</td>
																		<td>{(p.reasons || []).join(', ')}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</div>
											)}
										</section>

										<div className="superAdminMaintPreviewFooter">
											<p>
												Purge confirmation is not available on this screen. Continue only after reviewing
												the preview results. Purge also deletes projects owned by inactive users.
											</p>
											<Button
												bsStyle="danger"
												disabled={busy || (!inactivePreview && !orphanPreview)}
												onClick={() => {
													setPurgeUsersConfirm('');
													setPurgeOrphansConfirm('');
													setMaintStep('purge');
												}}>
												Continue to irreversible purge…
											</Button>
										</div>
									</>
								)
								: (
									<>
										<Button
											className="superAdminBackToPreview"
											disabled={busy}
											onClick={() => {
												setPurgeUsersConfirm('');
												setPurgeOrphansConfirm('');
												setMaintStep('preview');
											}}>
											← Back to Preview
										</Button>
										<section className="superAdminSection superAdminPurgeSection">
											<h3>Step 2 — Irreversible purge</h3>
											<p className="superAdminDangerBanner">
												You are about to permanently delete inactive users and/or orphaned projects.
												<strong> Purge also deletes projects they own.</strong> This cannot be undone
												from the SiteWalker UI.
											</p>
											{inactivePreview
												? (
													<div className="superAdminPurgeBlock">
														<h4>Inactive users</h4>
														<ul>
															<li>{inactivePreview.purgeableCount} purgeable users from the last preview</li>
															<li>Owned projects will be destroyed with those users</li>
															<li>Protected accounts are skipped</li>
														</ul>
														<FormGroup>
															<ControlLabel htmlFor="purge-users-confirm">
																Type <code>PURGE USERS</code> to confirm
															</ControlLabel>
															<FormControl
																id="purge-users-confirm"
																type="text"
																value={purgeUsersConfirm}
																onChange={e => setPurgeUsersConfirm(e.target.value)}
																autoComplete="off"
															/>
														</FormGroup>
														<Button
															bsStyle="danger"
															disabled={
																busy
																|| !inactivePreview.purgeableCount
																|| purgeUsersConfirm !== 'PURGE USERS'
															}
															onClick={purgeInactive}>
															Purge users &amp; owned projects
														</Button>
													</div>
												)
												: <p>No inactive-user preview is loaded. Go back and run Preview first.</p>}
											{orphanPreview
												? (
													<div className="superAdminPurgeBlock">
														<h4>Orphaned projects</h4>
														<p>Found <strong>{orphanPreview.count}</strong> orphaned project(s) in the last preview.</p>
														<FormGroup>
															<ControlLabel htmlFor="purge-orphans-confirm">
																Type <code>PURGE ORPHANS</code> to confirm
															</ControlLabel>
															<FormControl
																id="purge-orphans-confirm"
																type="text"
																value={purgeOrphansConfirm}
																onChange={e => setPurgeOrphansConfirm(e.target.value)}
																autoComplete="off"
															/>
														</FormGroup>
														<Button
															bsStyle="danger"
															disabled={
																busy
																|| !orphanPreview.count
																|| purgeOrphansConfirm !== 'PURGE ORPHANS'
															}
															onClick={purgeOrphans}>
															Purge all orphaned projects
														</Button>
													</div>
												)
												: <p>No orphaned-project preview is loaded. Go back and run Preview first.</p>}
										</section>
										<div className="superAdminMaintPreviewFooter">
											<Button
												onClick={() => setAdminTab(2)}>
												Open Project destruction tab…
											</Button>
										</div>
									</>
								)}
						</div>
					</Tab>
				</Tabs>
			</div>

			<ProjectDestroyModal
				show={Boolean(destroyTarget)}
				project={destroyTarget}
				onClose={() => setDestroyTarget(null)}
				onDestroyed={loadProjects}
			/>

			<Modal show={bulkUserActionOpen} onHide={() => setBulkUserActionOpen(false)} backdrop="static">
				<Modal.Header closeButton>
					<Modal.Title>Bulk user actions ({selectedUserIds.size})</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						Apply to the selected existing users. Password reset sends the same email-link flow
						as Forgot Password (expires in 1 week).
					</p>
					<ul className="superAdminMemberList">
						{users.filter(u => selectedUserIds.has(u.id)).slice(0, 40).map(u => (
							<li key={u.id}>{u.emailAddress}</li>
						))}
						{selectedUserIds.size > 40 && (
							<li>…and {selectedUserIds.size - 40} more</li>
						)}
					</ul>
					<FormGroup>
						<label className="checkbox-inline">
							<input
								type="checkbox"
								checked={bulkAddToProject}
								onChange={e => setBulkAddToProject(e.target.checked)}
							/>
							{' '}Add selected users to a project
						</label>
					</FormGroup>
					{bulkAddToProject && (
						<FormGroup>
							<ControlLabel>Project</ControlLabel>
							<FormControl
								componentClass="select"
								value={bulkProjectId}
								onChange={e => setBulkProjectId(e.target.value)}>
								<option value="">Select a project…</option>
								{projects.map(p => (
									<option key={p.id} value={p.id}>
										#{p.id} — {p.name}{p.ownerEmail ? ` (${p.ownerEmail})` : ''}
									</option>
								))}
							</FormControl>
						</FormGroup>
					)}
					<FormGroup>
						<label className="checkbox-inline">
							<input
								type="checkbox"
								checked={bulkResetPasswords}
								onChange={e => setBulkResetPasswords(e.target.checked)}
							/>
							{' '}Email password-reset links
						</label>
					</FormGroup>
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={() => setBulkUserActionOpen(false)} disabled={busy}>Cancel</Button>
					<Button
						bsStyle="primary"
						disabled={
							busy
							|| (!bulkAddToProject && !bulkResetPasswords)
							|| (bulkAddToProject && !bulkProjectId)
						}
						onClick={handleBulkUserActions}>
						Apply to selected users
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal show={bulkDestroyOpen} onHide={() => setBulkDestroyOpen(false)} backdrop="static">
				<Modal.Header closeButton>
					<Modal.Title>Destroy {selectedProjectCount} project(s)</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="superAdminDangerBanner">
						Bulk destroy force-deletes selected projects immediately (no per-project re-assign step).
						This removes SET_* tables, layouts, permissions, and dataset files.
					</p>
					{selectedSharedCount > 0 && (
						<p>
							<strong>{selectedSharedCount}</strong> of the selected projects are shared with other users.
						</p>
					)}
					<ul className="superAdminMemberList">
						{selectedProjects.slice(0, 40).map(p => (
							<li key={p.id}>#{p.id} — {p.name}{p.isShared ? ' (shared)' : ''}{p.isOrphan ? ' (orphan)' : ''}</li>
						))}
						{selectedProjects.length > 40 && (
							<li>…and {selectedProjects.length - 40} more</li>
						)}
					</ul>
					<FormGroup>
						<ControlLabel>Type <code>DESTROY</code> to confirm</ControlLabel>
						<FormControl
							type="text"
							value={bulkConfirm}
							onChange={e => setBulkConfirm(e.target.value)}
						/>
					</FormGroup>
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={() => setBulkDestroyOpen(false)} disabled={busy}>Cancel</Button>
					<Button
						bsStyle="danger"
						disabled={busy || bulkConfirm !== 'DESTROY'}
						onClick={handleBulkDestroyProjects}>
						Permanently destroy selected
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default SuperAdminPage;
